import { getCsrfHeader } from "./csrf";

/** Stable bill references remain opaque, especially case-sensitive state ids. */
export interface LegislationBill {
  bill_ref: string;
  title: string | null;
  identifier?: string | null;
  jurisdiction: string;
  session?: string | null;
  chamber?: string | null;
  latest_action_date?: string | null;
  latest_action_text?: string | null;
  source_updated_at?: string | null;
  synced_at?: string | null;
  source_url?: string | null;
  partial_data?: boolean;
  /** Discovery results: null when enactment could not be read for this page. */
  enactment?: LegislationEnactment | null;
  /** Discovery results: section status from the section layer; null when unreadable. */
  sections?: { status: string; section_count: number | null } | null;
}

export interface LegislationDiscovery {
  bills: LegislationBill[];
  offset: number;
  limit: number;
  has_more: boolean;
  next_offset: number | null;
  partial_data?: boolean;
  availability_note?: string;
}

export function legislationPath(billRef: string): string {
  return `/legislation/${encodeURIComponent(billRef)}`;
}

export function legislationLabel(bill: Pick<LegislationBill, "bill_ref" | "identifier">): string {
  return bill.identifier || (bill.bill_ref.startsWith("state:") ? bill.bill_ref.slice(6) : bill.bill_ref.toUpperCase());
}

export function safeSourceUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

export function formatLegislationDate(value: string | null | undefined): string | null {
  if (!value) return null;
  // Date-only government records should not move a day in local time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return null;
  return parsed.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** Enactment as read by src/domain/bill-intelligence.ts (federal law numbers, state classification). */
export interface LegislationEnactment {
  is_law: boolean | null;
  law_type?: string | null;
  law_number: string | number | null;
  citation?: string | null;
  became_law_date: string | null;
  progress_status?: string | null;
  basis: string | null;
}

const PROGRESS_LABELS: Record<string, string> = {
  introduced: "Introduced", in_committee: "In committee", reported: "Reported from committee",
  passed_chamber: "Passed one chamber", passed_both: "Passed both chambers", enacted: "Enacted",
  vetoed: "Vetoed", failed: "Failed", adopted: "Adopted",
};

/** One line: "Pub. L. 119-21 · became law 2025-07-04", "Not law · Passed one chamber", or not-available copy. */
export function enactmentLine(enactment: LegislationEnactment | null | undefined, scope: "federal" | "state"): string {
  if (!enactment) return scope === "state" ? "Not classified yet" : "Not available yet";
  if (enactment.is_law) {
    const law = enactment.citation ?? (enactment.law_number !== null && enactment.law_number !== undefined ? `Law ${enactment.law_number}` : "Enacted");
    const date = formatLegislationDate(enactment.became_law_date);
    return date ? `${law} · became law ${date}` : law;
  }
  const progress = enactment.progress_status ? PROGRESS_LABELS[enactment.progress_status] ?? enactment.progress_status.replace(/_/g, " ") : null;
  return progress ? `Not law · ${progress}` : "Not law";
}

/**
 * A read the account's plan does not include (402 plan_required, e.g. state
 * data on Free). Its message is the server's neutral statement of the limit;
 * pages show it in place of the data and queries do not retry it.
 */
export class PlanRequiredError extends Error {
  readonly code = "plan_required";
  constructor(message: string) {
    super(message);
    this.name = "PlanRequiredError";
  }
}
export function isPlanRequiredError(error: unknown): error is PlanRequiredError {
  return error instanceof PlanRequiredError;
}

export async function legislationRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      ...(method === "GET" ? {} : { "content-type": "application/json", ...getCsrfHeader() }),
      ...options.headers,
    },
  });
  const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
    if (response.status === 402 && record.error === "plan_required") {
      throw new PlanRequiredError(typeof record.message === "string" ? record.message : "This data requires a paid plan.");
    }
    const error = record.error;
    const message = typeof error === "string" ? error
      : error && typeof error === "object" && typeof (error as Record<string, unknown>).message === "string"
        ? String((error as Record<string, unknown>).message)
        : typeof record.message === "string" ? record.message : `Request failed (HTTP ${response.status}). Please try again.`;
    throw new Error(message);
  }
  return payload as T;
}
